import { useEffect, useCallback, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { usageTracker } from '../utils/usageTracker';
import { DEFAULT_LIMIT, useTicketListings } from '../data/useTicketListings';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { useDataFetchingClient } from 'data-fetching-client';
import { GET_TICKET } from '../data/useTicket';
import { GET_TICKET_THREADS } from '../data/useTicketThreads';
import { TicketListingsTable } from '../components/TicketListingsTable';
import { Flex } from 'foundations-components/components/Flex';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { TicketListingsGrid } from '../components/TicketListingsGrid';
import { Text } from 'foundations-components/components/Text';
import { useIsMobile } from '../hooks/useBreakpoint';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { TicketListingView } from '../constants/filters';
import { TicketListingsHeader } from '../components/TicketListingsHeader';
import { useIsPreview } from '../hooks/useIsPreview';
import { FAKE_THREAD_ID, augmentTicketWithPlaceholders } from '../data/previewMocks';
import { usePortalData } from '../data/usePortalData';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { PAGE_CONTENT_SPACING } from '../constants/layout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function TicketListings() {
  const isPreview = useIsPreview();
  const {
    data: portalData
  } = usePortalData();
  const ticketListingsSortAndFilters = useTicketListingsSortAndFilters();
  const isMobile = useIsMobile();
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const {
    view,
    offset,
    setOffset,
    searchString,
    sort,
    setSort,
    ticketView,
    statusFilter,
    createDateFilter,
    lastActivityFilter,
    hasActiveFilters
  } = ticketListingsSortAndFilters;
  const client = useDataFetchingClient();
  const {
    toTicketDetails
  } = useNavigationContext();
  const ticketListings = useTicketListings({
    sort,
    offset,
    ticketView,
    statusFilter,
    searchString,
    createDateFilter,
    lastActivityFilter
  });
  useEffect(() => {
    usageTracker.track('Customer Portal App', 'Page Viewed', {
      screen: 'ticket-listings'
    });
  }, []);
  const onRowClick = useCallback(ticketId => {
    if (ticketListings.loading) return;
    if (isPreview) {
      toTicketDetails(ticketId, FAKE_THREAD_ID);
      return;
    }
    usageTracker.track('Customer Portal App', 'Tickets Discovered', {
      action: 'ticket-expanded',
      screen: 'ticket-listings',
      initiator: view === TicketListingView.GRID || isMobile ? 'grid-card-click' : 'table-row-click'
    });
    toTicketDetails(ticketId);
  }, [ticketListings.loading, isPreview, view, isMobile, toTicketDetails]);
  const onRowHover = useCallback(ticketId => {
    if (ticketListings.loading || isPreview) return;
    void client.query({
      query: GET_TICKET,
      variables: {
        id: ticketId
      }
    });
    void client.query({
      query: GET_TICKET_THREADS,
      variables: {
        ticketId
      }
    });
  }, [client, ticketListings.loading, isPreview]);
  const data = useMemo(() => {
    if (ticketListings.loading) {
      return new Array(DEFAULT_LIMIT).fill(null).map((_, index) => ({
        hs_ticket_id: index + 1
      }));
    }
    if (!ticketListings.data) {
      return [];
    }
    if (isPreview) {
      var _portalData$configure;
      const configuredListProperties = (_portalData$configure = portalData === null || portalData === void 0 ? void 0 : portalData.configuredTicketListProperties) !== null && _portalData$configure !== void 0 ? _portalData$configure : [];
      return ticketListings.data.map(ticket => augmentTicketWithPlaceholders(ticket, configuredListProperties));
    }
    return ticketListings.data;
  }, [ticketListings.loading, ticketListings.data, isPreview, portalData === null || portalData === void 0 ? void 0 : portalData.configuredTicketListProperties]);
  const hasCreatedateColumn = useHasConfiguredColumn('createdate');
  const showSortingHelper = useMemo(() => !hasCreatedateColumn && sort.includes('-createdate'), [hasCreatedateColumn, sort]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "INDEX_LOAD",
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      "aria-live": "polite",
      "aria-atomic": "true",
      children: !ticketListings.loading && unescapedText('customer-portal-app.records', {
        count: ticketListings.total
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      paddingInline: isMobile ? '400' : PAGE_CONTENT_SPACING,
      paddingBlockStart: "200",
      children: [/*#__PURE__*/_jsx(TicketListingsHeader, {
        totalCount: ticketListings.total
      }), /*#__PURE__*/_jsxs("div", {
        children: [view === TicketListingView.GRID || isMobile ? /*#__PURE__*/_jsx(TicketListingsGrid, {
          data: data,
          isLoading: ticketListings.loading,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          isUsingPillBorderRadius: isUsingPillBorderRadius,
          error: !!ticketListings.error,
          hasActiveFilters: hasActiveFilters
        }) : /*#__PURE__*/_jsx(TicketListingsTable, {
          data: data,
          isLoading: ticketListings.loading,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          sort: sort,
          setSort: setSort,
          isUsingPillBorderRadius: isUsingPillBorderRadius,
          error: !!ticketListings.error,
          hasActiveFilters: hasActiveFilters
        }), showSortingHelper && /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          textAlign: "end",
          typography: "detail-200",
          color: "text-core-default",
          "data-test-id": "sorting-helper",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.sortingHelper"
          })
        })]
      }), ticketListings.total > DEFAULT_LIMIT && /*#__PURE__*/_jsx(Paginator, {
        "data-test-id": "listings-paginator",
        marginBlockStart: "600",
        pageCount: Math.ceil(ticketListings.total / DEFAULT_LIMIT),
        page: Math.floor(offset / DEFAULT_LIMIT) + 1,
        maxVisiblePageButtons: 7,
        onPageChange: e => setOffset((e.target.value - 1) * DEFAULT_LIMIT)
      })]
    })]
  });
}